import { useState, type FormEvent } from "react";
import { ApiError } from "../api/client";
import { Select } from "./Select";
import { CollapsePanel } from "../motion";
import type { Home } from "../hooks/useSettings";

export interface TodoFormValue {
  title: string;
  dueDate: string | null;
  homeId: string | null;
  tags: string[];
  notes: string | null;
}

export function TodoForm({
  homes, onSubmit, onCancel,
}: { homes: Home[]; onSubmit: (v: TodoFormValue) => Promise<unknown>; onCancel: () => void }) {
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [homeId, setHomeId] = useState("");
  const [tags, setTags] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        title: title.trim(),
        dueDate: dueDate ? `${dueDate}T00:00:00+08:00` : null,
        homeId: homeId || null,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        notes: notes.trim() || null,
      });
      // Only clear on success — a failed save must keep the typed fields.
      setTitle(""); setDueDate(""); setHomeId(""); setTags(""); setNotes("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save to-do — try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <CollapsePanel>
      <form className="form-card" onSubmit={submit}>
      <h2 className="section-title" style={{ margin: 0 }}>New to-do</h2>
      <label htmlFor="todo-title">Title</label>
      <input id="todo-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Do ICP assessment" autoFocus />
      <div className="form-row">
        <div>
          <label htmlFor="todo-due">Due date</label>
          <input id="todo-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>
        <div>
          <label htmlFor="todo-home">Home</label>
          <Select
            id="todo-home"
            value={homeId}
            onChange={setHomeId}
            options={[{ value: "", label: "— none —" }, ...homes.map((h) => ({ value: h.id, label: h.name }))]}
          />
        </div>
      </div>
      <label htmlFor="todo-tags">Tags (comma separated)</label>
      <input id="todo-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="icp, admission" />
      <label htmlFor="todo-notes">Notes</label>
      <textarea id="todo-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      {error ? <p className="error">{error}</p> : null}
          <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={submitting}>Add to-do</button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
      </form>
    </CollapsePanel>
  );
}
